import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Eye, EyeOff, Loader2, Upload } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = ["Insights", "Announcements", "Case Studies", "Research"];

/**
 * Admin blog manager — CRUD on posts with Markdown editor + live preview.
 * Renders inside the /admin console as its own tab.
 */
export default function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | postId
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post permanently?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      load();
    } catch (e) { setErr(formatApiError(e)); }
  };

  if (editing) {
    return (
      <PostEditor
        postId={editing === "new" ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div data-testid="admin-blog">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-[var(--navy)]">Insights</h2>
          <p className="text-xs text-[var(--muted)] mt-1">{posts.length} post{posts.length === 1 ? "" : "s"}</p>
        </div>
        <button
          onClick={() => setEditing("new")}
          className="btn btn-navy inline-flex items-center gap-2"
          data-testid="blog-new"
        >
          <Plus size={14} /> New post
        </button>
      </div>

      {err && <p className="text-xs text-red-600 mb-4">{err}</p>}

      {loading ? (
        <p className="text-sm text-[var(--muted)]">Loading…</p>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[var(--line)]">
          <p className="text-[var(--muted)] text-sm">No posts yet. Click "New post" to write your first insight.</p>
        </div>
      ) : (
        <div className="border border-[var(--line)]" data-testid="blog-posts-table">
          {posts.map((p, i) => (
            <div key={p.id} className={`flex items-start gap-4 p-4 ${i > 0 ? "border-t border-[var(--line)]" : ""}`}
                 data-testid={`blog-row-${p.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 uppercase tracking-widest ${
                    p.status === "published" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-700"
                  }`}>
                    {p.status === "published" ? <Eye size={10} /> : <EyeOff size={10} />}
                    {p.status}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest text-[var(--gold)]">{p.category}</span>
                </div>
                <h3 className="font-medium text-[var(--navy)] mt-1.5">{p.title}</h3>
                <p className="text-xs text-[var(--muted)] mt-0.5 font-mono">/blog/{p.slug}</p>
                {p.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {p.tags.map((t) => (
                      <span key={t} className="text-[10px] text-[var(--muted)]">#{t}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => setEditing(p.id)} data-testid={`blog-edit-${p.slug}`}
                        className="p-2 hover:bg-[var(--cream)] text-[var(--navy)]" title="Edit">
                  <Pencil size={14} />
                </button>
                <button onClick={() => del(p.id)} data-testid={`blog-delete-${p.slug}`}
                        className="p-2 hover:bg-red-50 text-red-600" title="Delete">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PostEditor({ postId, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", slug: "", excerpt: "", body_md: "", hero_image_url: "",
    category: "Insights", tags: "", author: "", status: "draft",
    seo_title: "", seo_description: "",
  });
  const [loading, setLoading] = useState(Boolean(postId));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("write"); // write | preview | seo

  useEffect(() => {
    if (!postId) return;
    api.get(`/admin/blog/${postId}`).then(({ data }) => {
      setForm({
        ...data,
        tags: (data.tags || []).join(", "),
      });
      setLoading(false);
    });
  }, [postId]);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const save = async (newStatus) => {
    setErr(""); setSaving(true);
    try {
      const payload = {
        title: form.title,
        slug: form.slug || undefined,
        excerpt: form.excerpt,
        body_md: form.body_md,
        hero_image_url: form.hero_image_url,
        category: form.category,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        author: form.author,
        status: newStatus || form.status,
        seo_title: form.seo_title,
        seo_description: form.seo_description,
      };
      if (postId) {
        await api.patch(`/admin/blog/${postId}`, payload);
      } else {
        await api.post("/admin/blog", payload);
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-[var(--muted)]">Loading…</p>;

  return (
    <div data-testid="blog-editor">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onClose} className="text-xs text-[var(--muted)] flex items-center gap-1.5 hover:text-[var(--navy)]"
                data-testid="blog-editor-back">
          <X size={13} /> Back to list
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => save("draft")} disabled={saving || !form.title || !form.body_md}
                  className="text-xs px-4 py-2 border border-[var(--line)] hover:border-[var(--navy)] disabled:opacity-50"
                  data-testid="blog-save-draft">
            Save draft
          </button>
          <button onClick={() => save("published")} disabled={saving || !form.title || !form.body_md}
                  className="btn btn-navy inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="blog-publish">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            {form.status === "published" ? "Update" : "Publish"}
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-4 border-b border-[var(--line)]">
        {["write", "preview", "seo"].map((t) => (
          <button key={t} onClick={() => setTab(t)}
                  className={`px-4 py-2 text-[11px] font-bold uppercase tracking-widest border-b-2 -mb-px ${
                    tab === t ? "text-[var(--navy)] border-[var(--gold)]" : "text-[var(--muted)] border-transparent"
                  }`}
                  data-testid={`blog-editor-tab-${t}`}>
            {t}
          </button>
        ))}
      </div>

      {err && <p className="text-xs text-red-600 mb-4 p-3 bg-red-50 border border-red-100">{err}</p>}

      {tab === "write" && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <input
              placeholder="Post title" value={form.title} onChange={set("title")}
              className="w-full text-2xl font-display px-4 py-3 border border-[var(--line)]"
              data-testid="blog-title" required
            />
            <input
              placeholder="url-slug (optional — auto-generated from title)"
              value={form.slug} onChange={set("slug")}
              className="w-full text-sm font-mono px-4 py-2 border border-[var(--line)]"
              data-testid="blog-slug"
            />
            <textarea
              placeholder="Short excerpt (shown in list + SEO)" value={form.excerpt} onChange={set("excerpt")}
              rows={2}
              className="w-full text-sm px-4 py-2 border border-[var(--line)]"
              data-testid="blog-excerpt"
            />
            <textarea
              placeholder="Body (Markdown supported — # headings, **bold**, [links](url), lists, code, tables)"
              value={form.body_md} onChange={set("body_md")}
              rows={22}
              className="w-full text-sm font-mono px-4 py-3 border border-[var(--line)] leading-relaxed"
              data-testid="blog-body"
              required
            />
          </div>
          <div className="space-y-4">
            <div className="field">
              <label className="field-label">Category</label>
              <select value={form.category} onChange={set("category")} data-testid="blog-category" className="w-full">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="field">
              <label className="field-label">Tags (comma-separated)</label>
              <input value={form.tags} onChange={set("tags")} placeholder="asam, iop, staffing"
                     data-testid="blog-tags" />
            </div>
            <div className="field">
              <label className="field-label">Author</label>
              <input value={form.author} onChange={set("author")} placeholder="Catalyst Team" data-testid="blog-author" />
            </div>
            <div className="field">
              <label className="field-label">Hero image</label>
              <div className="flex gap-2">
                <input value={form.hero_image_url} onChange={set("hero_image_url")}
                       placeholder="https://…or click upload"
                       data-testid="blog-hero" className="flex-1" />
                <label className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-[var(--line)] hover:border-[var(--navy)] cursor-pointer"
                       data-testid="blog-hero-upload-label">
                  {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {uploading ? "…" : "Upload"}
                  <input
                    type="file" accept="image/*" className="hidden"
                    data-testid="blog-hero-upload-input"
                    onChange={async (e) => {
                      const f = e.target.files?.[0]; if (!f) return;
                      setUploading(true); setErr("");
                      try {
                        const fd = new FormData(); fd.append("file", f);
                        const { data } = await api.post("/admin/blog/upload-image", fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                        });
                        setForm((s) => ({ ...s, hero_image_url: data.url }));
                      } catch (err) {
                        setErr(formatApiError(err));
                      } finally {
                        setUploading(false);
                        e.target.value = "";
                      }
                    }}
                  />
                </label>
              </div>
              {form.hero_image_url && (
                <img src={form.hero_image_url} alt="" className="mt-2 w-full aspect-video object-cover border border-[var(--line)]" />
              )}
            </div>
            <p className="text-xs text-[var(--muted)] pt-2">
              Current status: <span className="font-medium text-[var(--navy)]">{form.status}</span>
            </p>
          </div>
        </div>
      )}

      {tab === "preview" && (
        <article className="max-w-3xl mx-auto py-4">
          {form.hero_image_url && (
            <img src={form.hero_image_url} alt="" className="w-full aspect-[16/6] object-cover mb-8" />
          )}
          <p className="text-[11px] uppercase tracking-widest text-[var(--gold)]">{form.category}</p>
          <h1 className="font-display text-4xl text-[var(--navy)] mt-2">{form.title || "(untitled)"}</h1>
          {form.excerpt && <p className="text-lg text-[var(--muted)] mt-4">{form.excerpt}</p>}
          <div className="prose prose-neutral max-w-none mt-8" data-testid="blog-preview">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{form.body_md || "*(empty)*"}</ReactMarkdown>
          </div>
        </article>
      )}

      {tab === "seo" && (
        <div className="max-w-2xl space-y-4">
          <p className="text-sm text-[var(--muted)]">
            If empty, we'll use the post title and excerpt as SEO fallbacks.
          </p>
          <div className="field">
            <label className="field-label">SEO title (55-60 chars)</label>
            <input value={form.seo_title} onChange={set("seo_title")} maxLength={70} data-testid="blog-seo-title" />
            <p className="text-[10px] text-[var(--muted)] mt-1">{form.seo_title.length}/70</p>
          </div>
          <div className="field">
            <label className="field-label">SEO description (150-160 chars)</label>
            <textarea value={form.seo_description} onChange={set("seo_description")} rows={3} maxLength={200}
                      data-testid="blog-seo-description" className="w-full" />
            <p className="text-[10px] text-[var(--muted)] mt-1">{form.seo_description.length}/200</p>
          </div>
        </div>
      )}
    </div>
  );
}
